/**
 * The per-bar change label next to a comparison bar.
 *
 * Split out of `ChartWithControls` so the one rule that is easy to get
 * subtly wrong — what to show when there is nothing to compare against —
 * can be stated once and tested.
 */

/** Change up. */
export const DELTA_POSITIVE_COLOR = "#2e7d32";
/** Change down. */
export const DELTA_NEGATIVE_COLOR = "#c62828";
/**
 * No comparable change. Same grey as the value label.
 *
 * Used for "there was nothing in the comparison period", which is not a
 * performance result: a product that did not exist last year did not
 * *improve* infinitely. Colouring it green would read as a win the business
 * never had, and on a top-products chart it is the newest products —
 * the ones with the least history — that would light up brightest.
 */
export const DELTA_NEUTRAL_COLOR = "#666";

export type ComparisonDelta = { text: string; color: string };

/**
 * NOTE ON `color`, measured 2026-08-26: the horizontal-bar label renders the
 * whole string in one grey (`ChartWithControls`, label.color "#666") and uses
 * only `.text`, so `.color` is currently DECIDED HERE AND NOT DRAWN.
 *
 * Kept rather than dropped, because it is the decision — "nothing to compare
 * against is not a performance result" — and dropping it would leave that
 * decision nowhere. Arjen's requirement (an ∞ sign, but not coloured) is met
 * today by the label being uniformly grey; giving rises and falls their
 * sentiment colour would need ECharts rich text and is a separate ask.
 */

/**
 * Compute the delta label for one bar.
 *
 * `prevValue` is read from the aligned comparison row, so it is:
 * - a number when the member existed in the comparison period;
 * - `null`/`undefined` when it did not (the aligner leaves the metric
 *   absent rather than filling a zero — see `utils/comparisonAlignment.ts`).
 *
 * Returns `null` when no label should be drawn at all.
 */
export function computeComparisonDelta(
  currentValue: unknown,
  prevValue: unknown,
  formatPercent: (pct: number) => string,
): ComparisonDelta | null {
  const cur = typeof currentValue === "number" ? currentValue : Number(currentValue);
  if (!Number.isFinite(cur)) return null;

  const missing = prevValue == null;
  const prev = missing ? 0 : Number(prevValue);
  if (!missing && !Number.isFinite(prev)) return null;

  if (missing || prev === 0) {
    // Nothing then. "Absent" and "present but zero" are the same statement
    // to a reader, and neither is a percentage.
    if (cur === 0) return null; // nothing then, nothing now — say nothing
    return { text: `(${cur > 0 ? "+" : "-"}∞%)`, color: DELTA_NEUTRAL_COLOR };
  }

  const pct = ((cur - prev) / Math.abs(prev)) * 100;
  return {
    text: `(${pct >= 0 ? "+" : ""}${formatPercent(pct)}%)`,
    color: pct >= 0 ? DELTA_POSITIVE_COLOR : DELTA_NEGATIVE_COLOR,
  };
}
